
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, Inject, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxChange, MatCheckboxModule } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { subjectFromObs } from "core/rx-js";
import { FixedScrollDirective } from "core/shared-components";
import { BehaviorSubject, Subject, distinctUntilChanged, map } from "rxjs";
import { IChecklistDialogData } from "./checklist-dialog.service";

@Component({
    templateUrl: './checklist-dialog.component.html',
    styleUrls: ['./checklist-dialog.component.css'],
    standalone: true,
    imports: [
        MatCardModule,
        MatButtonModule,
        TranslateModule,
        MatCheckboxModule,
        MatIconModule,
        CommonModule,
        FixedScrollDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChecklistDialogComponent {
    private readonly _destroy = new Subject<void>()

    get valueExpr() {
        return this._data.valueExpr
    }
    get displayExpr() {
        return this._data.displayExpr;
    }
    get data() {
        return this._data.data
    }

    get title() {
        return this._data.title;
    }

    get defaultBtnText() {
        return this._data.defaultBtnText
    }

    private readonly valueSubject = new BehaviorSubject<any[]>([])
    get value() {
        return this.valueSubject.value;
    }
    private readonly selectedItemsSubject = subjectFromObs({
        initialValue: null,
        observable: this.valueSubject.pipe(
            map((value) => this.data.filter((element) => value.includes(element[this.valueExpr])))
        ),
        until: this._destroy
    })
    get selectedItems() {
        return this.selectedItemsSubject.value;
    }

    @ViewChild('cardHeader', { read: ElementRef })
    private _header: ElementRef<HTMLElement>
    get header() {
        return this._header?.nativeElement
    }

    readonly selectedDataObs = this.valueSubject.pipe(
        map((value) => this.data.map((element) => ({ ...element, selected: value.includes(element[this.valueExpr]) })))
    )

    readonly allSelectedObs = this.valueSubject.pipe(map((value) => value.length === this.data.length), distinctUntilChanged())

    constructor(
        @Inject(MAT_DIALOG_DATA) private readonly _data: IChecklistDialogData<any>,
        private readonly _matDialogRef: MatDialogRef<ChecklistDialogComponent>
    ) {

        if (this._data.defaultCheckAll) {
            this.checkAll();
        }

    }

    close() {
        this._matDialogRef.close();
    }

    submit() {
        this._matDialogRef.close(this.selectedItems);
    }

    checkAll() {
        this.valueSubject.next(this.data.map((element) => element[this.valueExpr]))
    }

    setChecked(element: any, ev: MatCheckboxChange) {
        if (ev.checked) {

            this.valueSubject.next([
                ...this.valueSubject.value,
                element[this.valueExpr]
            ])
            return;
        }

        this.valueSubject.next(this.value.filter(valuePart => valuePart !== element[this.valueExpr]));
    }

    ngOnDestroy() {

        this._destroy.next()
        this._destroy.complete()
    }

    trackByIdx(idx: number) {

        return idx;
    }

}